import { useState } from "react";
import { Button } from "flowbite-react";
import { ConversionHistoryRow } from "@/features/purchasePoints/components/ConversionHistory/ConversionHistoryRow";
import { PurchasePointsErrorState } from "@/features/purchasePoints/components/PurchasePointsErrorState";
import {
  BACK_TO_FIRST_PAGE_LABEL,
  CONVERSION_HISTORY_BEYOND_LAST_PAGE_MESSAGE,
  CONVERSION_HISTORY_EMPTY_MESSAGE,
  CONVERSION_HISTORY_ERROR_MESSAGE,
  CONVERSION_HISTORY_TITLE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import { isAccessCode } from "@/features/purchasePoints/errors/purchasePointsError";
import { usePurchasePointsConversions } from "@/features/purchasePoints/hooks/usePurchasePointsConversions";
import { readCatalogPagination } from "@/features/purchasePoints/utils/catalogPagination";
import { Pagination } from "@/shared/components/Pagination";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";

// Its own page, like the gains history it sits under: neither repaginates the
// catalogue above them.
const FIRST_PAGE = 1;

const TITLE_ID = "conversion-history-title";

export const ConversionHistoryList = () => {
  const [page, setPage] = useState(FIRST_PAGE);
  const {
    conversions, metadata, isLoading, isDisabled, error, retry,
  } = usePurchasePointsConversions(page);
  const { totalPages, isBeyondLastPage } = readCatalogPagination(metadata, page);

  // No employee attached to the session: nothing was requested, so claiming an
  // empty history would state a fact nobody checked.
  if (isDisabled) return null;

  const isEmpty = !isLoading && !error && conversions.length === 0;

  return (
    <section aria-labelledby={TITLE_ID} className="flex flex-col gap-4" data-testid="conversion-history">
      <H2 id={TITLE_ID}>{CONVERSION_HISTORY_TITLE}</H2>

      {isLoading && (
        <div
          role="status"
          aria-busy="true"
          aria-label={CONVERSION_HISTORY_TITLE}
          data-testid="conversion-history-skeleton"
          className="h-32 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"
        />
      )}

      {!isLoading && error && (
        <div
          data-testid="conversion-history-error"
          // An access refusal announces itself politely on its own; only a
          // failure has to interrupt.
          role={isAccessCode(error.code) ? undefined : "alert"}
        >
          <PurchasePointsErrorState
            error={error}
            onRetry={retry}
            onBackToFirstPage={page > FIRST_PAGE ? () => setPage(FIRST_PAGE) : undefined}
            fallbackMessage={CONVERSION_HISTORY_ERROR_MESSAGE}
          />
        </div>
      )}

      {isEmpty && (
        <div role="status" className="flex flex-col items-start gap-3" data-testid="conversion-history-empty">
          <P>{isBeyondLastPage ? CONVERSION_HISTORY_BEYOND_LAST_PAGE_MESSAGE : CONVERSION_HISTORY_EMPTY_MESSAGE}</P>
          {/* The page is local state: without this button neither a reload nor
              the browser's back button brings the employee back to page one. */}
          {isBeyondLastPage && (
            <Button color="light" size="sm" onClick={() => setPage(FIRST_PAGE)}>
              {BACK_TO_FIRST_PAGE_LABEL}
            </Button>
          )}
        </div>
      )}

      {!isLoading && !error && conversions.length > 0 && (
        // Tailwind's preflight strips the bullets, and with them the list role
        // Safari exposes to VoiceOver.
        <ul role="list" className="flex flex-col">
          {conversions.map((conversion) => (
            <ConversionHistoryRow key={conversion.id} conversion={conversion} />
          ))}
        </ul>
      )}

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        testId="conversion-history-pagination"
      />
    </section>
  );
};

ConversionHistoryList.displayName = "ConversionHistoryList";
